import React, { useId } from "react";
import type { QuickAction } from "../../utils/quickActions";

type Props = {
  actions: QuickAction[];
  onPick: (a: QuickAction & { enforce?: boolean }) => void;
  className?: string;
  sudsContext?: {
    awaitKey: string | null;
    mode?: string | null;
  };
};

function getSudsPrompt(context: Props["sudsContext"]): string | null {
  const isExposure = context?.mode?.trim().toUpperCase() === "EXPOSURE_COLLECTION";
  switch (context?.awaitKey?.trim().toUpperCase()) {
    case "SUDS1":
      return isExposure
        ? "Before the exposure, how anxious or distressed did you feel?"
        : "Before starting, how anxious or distressed do you feel right now?";
    case "SUDS2":
      return isExposure
        ? "After the exposure, how anxious or distressed did you feel?"
        : "After working on this homework, how anxious or distressed do you feel right now?";
    case "COPING_LOOP_SUDS":
      return "When you're finished practicing, how anxious or distressed do you feel right now?";
    default:
      return null;
  }
}

export default function QuickActionsBar({ actions, onPick, className, sudsContext }: Props) {
  const promptId = useId();
  if (!actions || actions.length === 0) return null;

  // Label only identified SUDS buttons in an active structured SUDS stage.
  // A numeric choice or a scale inferred from prose is not enough on its own.
  const hasSudsButtons = actions.some(
    (action) => /^(?:[0-9]|10)$/.test(action.value) && (
      action.group === "SUDS" ||
      action.group === "Scale0-10" ||
      /^suds-(?:[0-9]|10)$/.test(action.id)
    ),
  ) && !actions.some(
    (action) => action.group === "HomeworkSelect" || action.group === "CopingSelect",
  );
  const sudsPrompt = hasSudsButtons ? getSudsPrompt(sudsContext) : null;
  // The deployed coping-loop workflow supports skipping this rating.
  const showSkipInstruction = Boolean(
    sudsPrompt && sudsContext?.awaitKey?.trim().toUpperCase() === "COPING_LOOP_SUDS",
  );

  // Check if this is a numeric scale group (SUDS or Scale0-10)
  const isScaleGroup = actions.every(
    (a) => a.group === "SUDS" || a.group === "Scale0-10"
  );

  // Check if this is a homework selection group
  const isHomeworkGroup = actions.every((a) => a.group === "HomeworkSelect");

  // 🆕 Check if this is a coping selection group
  const isCopingGroup = actions.every((a) => a.group === "CopingSelect");

  // Compact sizing for scale, homework selection, and coping selection
  // Centered row for homework + coping
  const containerLayout = isScaleGroup
    ? "justify-between flex-nowrap"
    : "justify-center flex-wrap gap-2";

  const useCompactSizing = isScaleGroup || isHomeworkGroup || isCopingGroup;

  return (
    <div
      className={["w-full mb-2", className].filter(Boolean).join(" ")}
      role="toolbar"
      aria-label={sudsPrompt ? undefined : "Quick reply options"}
      aria-labelledby={sudsPrompt ? promptId : undefined}
      aria-describedby={showSkipInstruction ? `${promptId}-help` : undefined}
      data-efa-quick-actions
    >
      {sudsPrompt ? (
        <p id={promptId} className="mx-auto mb-2 max-w-3xl px-2 text-center text-sm leading-5 text-gray-700">
          {sudsPrompt}
        </p>
      ) : null}
      {showSkipInstruction ? (
        <p id={`${promptId}-help`} className="mx-auto -mt-1 mb-2 max-w-3xl px-2 text-center text-xs leading-4 text-gray-500">
          Select a number from 0 to 10, or type or say 'skip' to continue.
        </p>
      ) : null}
      <div className={`mx-auto max-w-3xl flex ${containerLayout} px-2`}>
        {actions.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() =>
              onPick({ ...a, enforce: useCompactSizing }) // enforce for SUDS, HomeworkSelect, CopingSelect
            }
            aria-keyshortcuts={a.shortcut?.toUpperCase()}
            aria-label={
              a.shortcut
                ? `${a.label} (shortcut ${a.shortcut.toUpperCase()})`
                : a.label
            }
            className={`inline-flex shrink-0 items-center justify-center rounded-2xl border border-gray-200 shadow-sm font-medium select-none 
              focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-400 active:scale-[0.99]
              ${useCompactSizing
                ? "px-2 py-1.5 text-xs min-w-[28px]" // compact mode
                : "px-3 py-2 text-sm"                // normal mode
              }
            `}
          >
            <span className="tabular-nums">{a.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
